import CommentPolicyHint from "../components/CommentPolicyHint";
import { usePermission } from "../hooks/usePermission";
import { useAuth } from "../contexts/AuthContext";
import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../api/api";
import toast from "react-hot-toast";
import WorkLogTextDisplay from "../components/WorkLogTextDisplay";
import { SizeBadge, calcTaskSize } from "../utils/sizing";
import {
  Plus, Play, Square, X, Pencil, Trash2,
  Clock, LayoutList, Kanban, Link, User,
  ChevronRight, MessageSquare, Activity,
  FileText, Timer, Calendar, Building2, GripVertical, Filter,
} from "lucide-react";

/* ── constants ──────────────────────────────────────────────── */
const STATUSES = ["OPEN", "IN_PROGRESS", "ON_HOLD", "COMPLETED"] as const;

const STATUS_META: Record<string, { label: string; badge: string; col: string; border: string; dot: string; dropBg: string }> = {
  OPEN:        { label: "Open",        badge: "bg-gray-100 text-gray-700",    col: "bg-gray-50",   border: "border-gray-200",  dot: "bg-gray-400",   dropBg: "bg-gray-100/80"    },
  IN_PROGRESS: { label: "In Progress", badge: "bg-yellow-100 text-yellow-700",col: "bg-yellow-50", border: "border-yellow-300", dot: "bg-yellow-400", dropBg: "bg-yellow-100/80"  },
  ON_HOLD:     { label: "On Hold",     badge: "bg-orange-100 text-orange-700",col: "bg-orange-50", border: "border-orange-300", dot: "bg-orange-400", dropBg: "bg-orange-100/80"  },
  COMPLETED:   { label: "Completed",   badge: "bg-green-100 text-green-700",  col: "bg-green-50",  border: "border-green-300",  dot: "bg-green-500",  dropBg: "bg-green-100/80"   },
};

const DEPARTMENTS = ["Technical Support","Development","Customer Support","Billing","Other"];
const fmt = (sec: number) => {
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60 > 0 ? (m % 60) + "m" : ""}`.trim();
};

// Parses flexible time input: "15" (min), "1.5" (hr), "1h 30m", "90m"
const parseToMinutes = (val: string, unit: string): number => {
  const s = (val || "").trim();
  // "1h 30m" or "1h30m"
  const hm = s.match(/^(\d+(?:\.\d+)?)h\s*(\d+)m?$/i);
  if (hm) return Math.max(1, Math.round(parseFloat(hm[1]) * 60 + parseInt(hm[2])));
  // "1.5h" or "2h"
  const h = s.match(/^(\d+(?:\.\d+)?)h$/i);
  if (h) return Math.max(1, Math.round(parseFloat(h[1]) * 60));
  // "30m"
  const m = s.match(/^(\d+)m$/i);
  if (m) return Math.max(1, parseInt(m[1]));
  // plain number — use selected unit
  const n = parseFloat(s);
  if (isNaN(n) || n <= 0) return 0;
  return unit === "hr" ? Math.max(1, Math.round(n * 60)) : Math.max(1, Math.round(n));
};
const fmtMin = (m: number) => { const h = Math.floor(m/60); return `${h}:${String(m%60).padStart(2,'0')}`; };
const fmtH = (hours: number) => {
  const m = Math.round((hours || 0) * 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
};
const blankTask = { title: "", description: "", department: "", assignedToId: "", referenceLinks: [""] };

const parseLinks = (val: any): string[] => {
  if (Array.isArray(val)) return val.length ? val : [""];
  if (typeof val === "string" && val.startsWith("[")) {
    try { const arr = JSON.parse(val); return arr.length ? arr : [""]; } catch {}
  }
  return val ? [val] : [""];
};
const serializeLinks = (links: string[]): string => {
  const filtered = links.map(l => l.trim()).filter(Boolean);
  return filtered.length <= 1 ? (filtered[0] || "") : JSON.stringify(filtered);
};
const blankLog  = { minutes: "", unit: "min", date: new Date().toISOString().slice(0, 10), comment: "" };

/* ── CommentInput ─────────────────────────────────────────── */
const CommentInput: React.FC<{ taskId: string; onSaved: () => void }> = ({ taskId, onSaved }) => {
  const [text, setText] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/tasks/${taskId}/comments`, { content: text });
      setText("");
      onSaved();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="flex gap-2">
      <input
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Write a comment..."
        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
      />
      <button type="submit" disabled={saving || !text.trim()}
        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
        {saving ? "..." : "Post"}
      </button>
    </form>
  );
};



/* -- SubTaskCommentInput --------------------------------------------- */
const SubTaskCommentInput: React.FC<{ subTaskId: string; onSaved: () => void }> = ({ subTaskId, onSaved }) => {
  const [text, setText] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/tasks/subtasks/${subTaskId}/comments`, { content: text });
      setText("");
      onSaved();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="flex gap-2">
      <input value={text} onChange={e => setText(e.target.value)} placeholder="Write a comment..."
        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500"/>
      <button type="submit" disabled={saving || !text.trim()}
        className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed">
        {saving ? "..." : "Post"}
      </button>
    </form>
  );
};

/* -- SubTaskDetailModal ---------------------------------------------- */
const SubTaskDetailModal: React.FC<{
  subTaskId: string;
  onClose: () => void;
  onUpdated: () => void;
}> = ({ subTaskId, onClose, onUpdated }) => {
  const [st, setSt] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState<"overview"|"worklogs"|"comments">("overview");
  const [showLogTime, setShowLogTime] = React.useState(false);
  const [logForm, setLogForm] = React.useState({ minutes: "", unit: "min", date: new Date().toISOString().slice(0, 10), comment: "" });

  const load = React.useCallback(() => {
    setLoading(true);
    api.get(`/tasks/subtasks/${subTaskId}/detail`)
      .then(r => setSt(r.data))
      .catch(() => toast.error("Failed to load subtask"))
      .finally(() => setLoading(false));
  }, [subTaskId]);

  React.useEffect(() => { load(); }, [load]);

  const submitLog = async (e: React.FormEvent) => {
    e.preventDefault();
    const _mins0 = parseToMinutes(logForm.minutes, logForm.unit || "min") || 0;
    try {
      const _pc = await api.get(`/comment-policy/check?minutes=${_mins0}`);
      const _v: any = _pc.data || {};
      const _trim = ((logForm as any).comment || "").trim();
      if (_v.required && (!_trim || _trim.length < (_v.minLength || 0))) {
        toast.error(`Comment of ${_v.minLength}+ chars required for ${_mins0}m`); return;
      }
      if (!_v.required && _v.recommended && !_trim) {
        if (!window.confirm(`You worked ${_mins0} minutes — a short comment is recommended. Save without one?`)) return;
      }
    } catch (_e) {}
    const mins = parseToMinutes(logForm.minutes, logForm.unit || "min");
    if (!mins || mins < 1) { toast.error("Minimum 1 minute"); return; }
    try {
      await api.post(`/tasks/subtasks/${subTaskId}/work-logs`, {
        minutes: mins, date: logForm.date, comment: logForm.comment,
      });
      toast.success("Time logged!");
      setShowLogTime(false);
      setLogForm({ minutes: "", unit: "min", date: new Date().toISOString().slice(0, 10), comment: "" });
      load();
      onUpdated();
    } catch (err: any) { toast.error(err?.response?.data?.message || "Failed to log time"); }
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8" onClick={e => e.stopPropagation()}>
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto"/>
      </div>
    </div>
  );

  if (!st) return null;

  const statusMeta = STATUS_META[st.status] || STATUS_META.OPEN;
  const totalMins = st.totalLoggedMinutes || 0;

  const tabs = [
    { key: "overview",  label: "Overview",   icon: <FileText size={13}/> },
    { key: "worklogs",  label: "Work Logs",  icon: <Clock size={13}/>, count: st.workLogs?.length },
    { key: "comments",  label: "Comments",   icon: <MessageSquare size={13}/>, count: st.comments?.length },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="p-5 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0 pr-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-purple-500">Sub-Task</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${statusMeta.badge}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dot}`}/>
                  {statusMeta.label}
                </span>
              </div>
              <h2 className="text-lg font-bold text-gray-900 leading-tight">{st.title}</h2>
              {st.task && <p className="text-xs text-gray-400 mt-1">Parent: <span className="text-gray-600 font-medium">{st.task.title}</span></p>}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5">
              <X size={18}/>
            </button>
          </div>

          {/* Stats */}
          <div className="flex gap-4 mt-4">
            <div className="bg-blue-50 rounded-lg px-4 py-2 flex-1 text-center">
              <p className="text-[10px] text-blue-500 font-medium">Logged</p>
              <p className="text-lg font-bold text-blue-700">{fmtMin(totalMins)}</p>
            </div>
            <div className="bg-purple-50 rounded-lg px-4 py-2 flex-1 text-center">
              <p className="text-[10px] text-purple-500 font-medium">Work Entries</p>
              <p className="text-lg font-bold text-purple-700">{st.workLogs?.length || 0}</p>
            </div>
            <div className="bg-gray-50 rounded-lg px-4 py-2 flex-1 text-center">
              <p className="text-[10px] text-gray-500 font-medium">Comments</p>
              <p className="text-lg font-bold text-gray-700">{st.comments?.length || 0}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-white px-2">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key as any)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? "border-purple-600 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}>
              {tab.icon} {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="bg-gray-100 text-gray-600 text-[10px] px-1.5 py-0.5 rounded-full">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-5">

          {/* Overview */}
          {activeTab === "overview" && (
            <div className="space-y-4">
              {st.description && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</h4>
                  <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-4">{st.description}</p>
                </div>
              )}
              <div className="space-y-3">
                {st.assignedTo && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 w-24 flex items-center gap-1"><User size={11}/> Assigned To</span>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[10px] font-bold">
                        {st.assignedTo.name[0].toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-gray-800">{st.assignedTo.name}</span>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-24 flex items-center gap-1"><Calendar size={11}/> Created</span>
                  <span className="text-sm text-gray-700">{new Date(st.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
                </div>
              </div>
              <div className="pt-3 border-t border-gray-100">
                <button onClick={() => setShowLogTime(true)}
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 text-sm font-medium w-full justify-center">
                  <Clock size={14}/> Log Time
                </button>
              </div>
            </div>
          )}

          {/* Work Logs */}
          {activeTab === "worklogs" && (
            <div className="space-y-3">
              <button onClick={() => setShowLogTime(true)}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">
                <Plus size={14}/> Log Time
              </button>
              {(!st.workLogs || st.workLogs.length === 0) ? (
                <div className="text-center py-10 text-gray-400">
                  <Clock size={28} className="mx-auto mb-2 opacity-30"/>
                  <p className="text-sm">No work logged yet</p>
                </div>
              ) : (
                st.workLogs.map((log: any) => (
                  <div key={log.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-200 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {(log.employee?.name || "?")[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{log.employee?.name}</p>
                          <p className="text-xs text-gray-400">{new Date(log.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</p>
                        </div>
                      </div>
                      <span className="text-lg font-bold text-blue-600">{fmtMin(log.minutes)}</span>
                    </div>
                    <div className="ml-9">
                      <WorkLogTextDisplay comment={log.comment} description={log.description} />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Comments */}
          {activeTab === "comments" && (
            <div className="space-y-3">
              <SubTaskCommentInput subTaskId={subTaskId} onSaved={load} />
              {(!st.comments || st.comments.length === 0) ? (
                <div className="text-center py-10 text-gray-400">
                  <MessageSquare size={28} className="mx-auto mb-2 opacity-30"/>
                  <p className="text-sm">No comments yet</p>
                </div>
              ) : (
                st.comments.map((c: any) => (
                  <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold">
                        {(c.author?.name || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{c.author?.name}</p>
                        <p className="text-xs text-gray-400">{new Date(c.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} {new Date(c.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed ml-9">{c.content}</p>
                  </div>
                ))
              )}
            </div>
          )}

        </div>

        {/* Log Time Inline Panel */}
        {showLogTime && (
          <div className="border-t border-gray-200 bg-gray-50 p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1"><Clock size={14}/> Log Time</h4>
              <button onClick={() => setShowLogTime(false)} className="text-gray-400 hover:text-gray-600"><X size={16}/></button>
            </div>
            <form onSubmit={submitLog} className="space-y-3">
              <div className="flex gap-2">
                <input type="text" value={logForm.minutes}
                  onChange={e => setLogForm(f => ({ ...f, minutes: e.target.value }))}
                  required placeholder={logForm.unit==="hr" ? "e.g. 1.5" : "e.g. 30"}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none"/>
                <div className="flex border border-gray-300 rounded-lg overflow-hidden text-sm">
                  <button type="button" onClick={() => setLogForm(f=>({...f,unit:"min"}))}
                    className={`px-3 py-2 font-medium transition-colors ${(logForm.unit||"min")==="min"?"bg-purple-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>min</button>
                  <button type="button" onClick={() => setLogForm(f=>({...f,unit:"hr"}))}
                    className={`px-3 py-2 font-medium transition-colors ${logForm.unit==="hr"?"bg-purple-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>hr</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={logForm.date} onChange={e => setLogForm(f => ({ ...f, date: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none"/>
                <input type="text" value={logForm.comment} onChange={e => setLogForm(f => ({ ...f, comment: e.target.value }))}
                  placeholder="Note (optional)"
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none"/>
              </div>
              <button type="submit"
                className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 text-sm font-medium">
                Save Work Log
              </button>
            </form>
          </div>
        )}

      </div>
    </div>
  );
};

/* ── TaskDetailDrawer ─────────────────────────────────────── */
const TaskDetailDrawer: React.FC<{ taskId: string; onClose: () => void; onEdit: () => void }> = ({ taskId, onClose, onEdit }) => {
  const { user, isAdmin } = useAuth();
  const isManagerOrAdmin = isAdmin || user?.role === "MANAGER" || user?.role === "DEPARTMENT_HEAD";
  const [task, setTask] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<"overview"|"worklogs"|"comments"|"activity">("overview");
  const [loading, setLoading] = useState(true);
  const [editingST, setEditingST] = useState<any|null>(null);
  const [editSTForm, setEditSTForm] = useState({ title: "", status: "" });
  const [viewSubTaskId, setViewSubTaskId] = useState<string|null>(null);
  const [classifying, setClassifying] = useState(false);
  const classifyTask = async (level: string) => {
    setClassifying(true);
    try {
      await api.put(`/tasks/${taskId}/classify`, { workLevel: level });
      toast.success(`Classified as ${level}`);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally { setClassifying(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/tasks/${taskId}`)
      .then(r => setTask(r.data))
      .finally(() => setLoading(false));
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const openEditST = (st: any) => {
    setEditingST(st);
    setEditSTForm({ title: st.title || "", status: st.status || "OPEN" });
  };
  const submitEditST = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingST) return;
    try {
      await api.put(`/tasks/subtasks/${editingST.id}`, editSTForm);
      toast.success("Sub-task updated!");
      setEditingST(null);
      load();
    } catch { toast.error("Failed to update"); }
  };
  const deleteST = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this sub-task?")) return;
    try {
      await api.delete(`/tasks/subtasks/${id}`);
      toast.success("Sub-task deleted!");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white h-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );

  if (!task) return null;

  const totalLogged = task.totalLoggedMinutes || 0;
  const timerTime = fmt(task.timerTotal || 0);
  const totalLoggedFmt = fmtMin(totalLogged);
  const statusMeta = STATUS_META[task.status] || STATUS_META.OPEN;

  const tabs = [
    { key: "overview",  label: "Overview",   icon: <FileText size={13}/> },
    { key: "worklogs",  label: "Work Logs",  icon: <Clock size={13}/>, count: task.workLogs?.length },
    { key: "comments",  label: "Comments",   icon: <MessageSquare size={13}/>, count: task.comments?.length },
    { key: "activity",  label: "Activity",   icon: <Activity size={13}/>, count: task.activityLogs?.length },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white h-full flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-200 bg-white">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 ${statusMeta.badge}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dot}`} />
                {statusMeta.label}
              </span>
              {task.department && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Building2 size={10}/> {task.department}
                </span>
              )}
            </div>
            <h2 className="text-xl font-bold text-gray-900 leading-tight">{task.title}</h2>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isManagerOrAdmin && (
              <div className="flex items-center gap-1 border border-gray-200 rounded-lg px-2 py-1">
                <span className="text-[10px] text-gray-500 uppercase mr-1">Points:</span>
                {(["simple","medium","critical"] as const).map(lvl => {
                  const cur = task.workLevel === lvl;
                  const meta = { simple: {c:"green", pt:1, e:"🟢"}, medium: {c:"yellow", pt:3, e:"🟡"}, critical: {c:"red", pt:5, e:"🔴"} }[lvl];
                  return (
                    <button key={lvl} disabled={classifying} onClick={() => classifyTask(lvl)}
                      className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${
                        cur ? `bg-${meta.c}-100 text-${meta.c}-700 border border-${meta.c}-300` : `hover:bg-${meta.c}-50 text-gray-500`
                      }`}>
                      {meta.e} {meta.pt}
                    </button>
                  );
                })}
              </div>
            )}
            {!isManagerOrAdmin && task.workLevel && (
              <span className="text-[10px] text-gray-500 border border-gray-200 rounded-lg px-2 py-1">
                Points: {task.workPoints}
              </span>
            )}
            <button onClick={onEdit}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 border border-gray-200 hover:border-blue-300 px-3 py-1.5 rounded-lg transition-colors">
              <Pencil size={12}/> Edit
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5">
              <X size={18}/>
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 gap-px bg-gray-200 border-b border-gray-200">
          <div className="bg-blue-50 px-4 py-3 text-center">
            <p className="text-xs text-blue-500 flex items-center justify-center gap-1"><Clock size={11}/> Logged Hours</p>
            <p className="font-bold text-blue-700 text-lg mt-0.5">{fmtMin(totalLogged)}</p>
          </div>
          <div className="bg-white px-4 py-3 text-center">
            <p className="text-xs text-gray-500 flex items-center justify-center gap-1"><FileText size={11}/> Work Entries</p>
            <p className="font-bold text-gray-800 text-sm mt-0.5">{task.workLogs?.length || 0}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-white">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key as any)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}>
              {tab.icon} {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="bg-gray-100 text-gray-600 text-[10px] px-1.5 py-0.5 rounded-full">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-5">

          {/* ── Overview ─────────────────────────────────────── */}
          {activeTab === "overview" && (
            <div className="space-y-5">
              {task.description && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</h4>
                  <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-4">{task.description}</p>
                </div>
              )}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Details</h4>
                <div className="space-y-3">
                  {task.assignedTo && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 w-24 flex items-center gap-1"><User size={11}/> Assigned To</span>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold">
                          {task.assignedTo.name[0].toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-gray-800">{task.assignedTo.name}</span>
                      </div>
                    </div>
                  )}
                  {task.project && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 w-24">Project</span>
                      <span className="text-sm font-medium text-purple-600">{task.project.name}</span>
                    </div>
                  )}
                  {task.department && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 w-24 flex items-center gap-1"><Building2 size={11}/> Department</span>
                      <span className="text-sm text-gray-700">{task.department}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 w-24 flex items-center gap-1"><Calendar size={11}/> Created</span>
                    <span className="text-sm text-gray-700">{new Date(task.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
                  </div>
                  {task.referenceLink && (
                    <div className="flex items-start gap-3">
                      <span className="text-xs text-gray-400 w-24 flex items-center gap-1 mt-0.5"><Link size={11}/> Reference</span>
                      <div className="flex flex-col gap-1">
                        {parseLinks(task.referenceLink).filter(Boolean).map((lnk: string, i: number) => (
                          <a key={i} href={lnk.startsWith("http") ? lnk : `https://${lnk}`} target="_blank" rel="noreferrer"
                            className="text-sm text-blue-500 hover:underline truncate max-w-xs">{lnk}</a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {task.subTasks?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Sub-tasks ({task.subTasks.length})</h4>
                  <div className="space-y-2">
                    {task.subTasks.map((st: any) => (
                      <div key={st.id}
                        onClick={() => setViewSubTaskId(st.id)}
                        className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100 cursor-pointer hover:bg-purple-50 hover:border-purple-200 transition-colors group">
                        <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_META[st.status]?.dot || "bg-gray-300"}`} />
                          <span className="text-sm text-gray-700 group-hover:text-purple-700 truncate">{st.title}</span>
                          {st.assignedTo && (
                            <span className="text-[10px] text-gray-400 flex items-center gap-0.5 flex-shrink-0">
                              <User size={9}/> {st.assignedTo.name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400 flex-shrink-0">
                          {st.totalLoggedMinutes > 0 && (
                            <span className="text-blue-500 flex items-center gap-0.5 font-medium">
                              <Clock size={10}/> {fmtMin(st.totalLoggedMinutes)}
                            </span>
                          )}
                          {(st.workLogs?.length > 0 || st.comments?.length > 0) && (
                            <div className="flex items-center gap-1.5">
                              {st.workLogs?.length > 0 && (
                                <span className="flex items-center gap-0.5 text-blue-400" title={`${st.workLogs.length} work entries`}>
                                  <Timer size={10}/> {st.workLogs.length}
                                </span>
                              )}
                              {st.comments?.length > 0 && (
                                <span className="flex items-center gap-0.5 text-purple-400" title={`${st.comments.length} comments`}>
                                  <MessageSquare size={10}/> {st.comments.length}
                                </span>
                              )}
                            </div>
                          )}
                          <span className={`px-2 py-0.5 rounded-full ${STATUS_META[st.status]?.badge || "bg-gray-100 text-gray-600"}`}>
                            {STATUS_META[st.status]?.label || st.status}
                          </span>
                          <button onClick={(e) => { e.stopPropagation(); openEditST(st); }} title="Edit Sub-task"
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                            <Pencil size={12}/>
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); deleteST(st.id); }} title="Delete Sub-task"
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                            <Trash2 size={12}/>
                          </button>
                        </div>
                        </div>
                        {st.description && (
                          <p className="text-xs text-gray-400 mt-1.5 ml-4 leading-relaxed line-clamp-2" dir="auto">{st.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Work Logs ────────────────────────────────────── */}
          {activeTab === "worklogs" && (
            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-500 font-medium">Total Hours Logged</p>
                  <p className="text-2xl font-bold text-blue-700">{totalLogged}h</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-blue-400">{task.workLogs?.length || 0} entries</p>
                </div>
              </div>
              {task.workLogs?.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Clock size={32} className="mx-auto mb-2 opacity-30"/>
                  <p>No work logged yet</p>
                </div>
              ) : (
                task.workLogs?.map((log: any) => (
                  <div key={log.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-200 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {(log.employee?.name || "?")[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{log.employee?.name}</p>
                          <p className="text-xs text-gray-400">{new Date(log.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</p>
                        </div>
                      </div>
                      <span className="text-lg font-bold text-blue-600">{fmtMin(log.minutes)}</span>
                    </div>
                    <div className="ml-9">
                      <WorkLogTextDisplay comment={log.comment} description={log.description} />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── Comments ─────────────────────────────────────── */}
          {activeTab === "comments" && (
            <div className="space-y-3">
              {/* Comment input */}
              <CommentInput taskId={taskId} onSaved={load} />
              {task.comments?.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <MessageSquare size={32} className="mx-auto mb-2 opacity-30"/>
                  <p>No comments yet — be the first to comment!</p>
                </div>
              ) : (
                task.comments?.map((c: any) => (
                  <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold">
                        {(c.author?.name || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{c.author?.name}</p>
                        <p className="text-xs text-gray-400">{new Date(c.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed ml-9">{c.content}</p>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── Activity ─────────────────────────────────────── */}
          {activeTab === "activity" && (
            <div className="space-y-1">
              {task.activityLogs?.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Activity size={32} className="mx-auto mb-2 opacity-30"/>
                  <p>No activity yet</p>
                </div>
              ) : (
                task.activityLogs?.map((a: any, idx: number) => (
                  <div key={a.id} className="flex gap-3 py-3 border-b border-gray-100 last:border-0">
                    <div className="flex flex-col items-center">
                      <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {(a.user?.name || a.userName || "?")[0].toUpperCase()}
                      </div>
                      {idx < task.activityLogs.length - 1 && <div className="w-0.5 flex-1 bg-gray-100 mt-1"/>}
                    </div>
                    <div className="flex-1 pb-1">
                      <p className="text-sm text-gray-700">{a.action}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {a.user?.name || a.userName} • {new Date(a.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} {new Date(a.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

        </div>

        {/* Sub-Task Detail Modal */}
        {viewSubTaskId && (
          <SubTaskDetailModal
            subTaskId={viewSubTaskId}
            onClose={() => setViewSubTaskId(null)}
            onUpdated={() => { load(); }}
          />
        )}

        {/* Edit Sub-Task Inline Modal */}
        {editingST && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Edit Sub-Task</h3>
                <button onClick={() => setEditingST(null)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
              </div>
              <form onSubmit={submitEditST} className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input value={editSTForm.title} onChange={e => setEditSTForm(f => ({...f, title: e.target.value}))} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={editSTForm.status} onChange={e => setEditSTForm(f => ({...f, status: e.target.value}))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                    <option value="OPEN">Open</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="ON_HOLD">On Hold</option>
                    <option value="COMPLETED">Completed</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-2 border-t border-gray-100">
                  <button type="button" onClick={() => setEditingST(null)}
                    className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                  <button type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm hover:bg-blue-700 font-medium">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

/* ── TaskCard ─────────────────────────────────────────────── */
interface CardProps {
  t: any; compact?: boolean; isDragging?: boolean; tick?: number;
  onView:   (t: any)    => void;
  onStart:  (id: string) => void;
  onStop:   (id: string) => void;
  onLog:    (t: any)    => void;
  onEdit:   (t: any)    => void;
  onDelete: (id: string) => void;
  onStatus: (id: string, s: string) => void;
  onDragStart?: (e: React.DragEvent, id: string) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

const TaskCard: React.FC<CardProps> = ({
  t, compact = false, isDragging = false, tick: _tick = 0,
  onView, onStart, onStop, onLog, onEdit, onDelete, onStatus, onDragStart,
  canEdit: canEditProp = true, canDelete: canDeleteProp = true,
}) => {
  const liveElapsed = t.timerStart
    ? Math.floor((Date.now() - new Date(t.timerStart).getTime()) / 1000)
    : 0;
  const totalSec = (t.timerTotal || 0) + liveElapsed;
  return (
  <div
    draggable={!!onDragStart}
    onDragStart={onDragStart ? e => onDragStart(e, t.id) : undefined}
    className={`bg-white rounded-xl border shadow-sm flex flex-col gap-2 transition-all
      ${compact ? "p-3" : "p-4"}
      ${isDragging ? "opacity-40 scale-95 border-blue-300 shadow-blue-100" : "border-gray-200 hover:border-gray-300"}
      ${onDragStart ? "cursor-grab active:cursor-grabbing" : ""}
    `}
  >
    <div className="flex items-start justify-between gap-2">
      {/* Drag handle + Clickable title */}
      <div className="flex items-start gap-1 flex-1 min-w-0">
        {onDragStart && (
          <GripVertical size={14} className="text-gray-300 hover:text-gray-400 mt-0.5 flex-shrink-0 cursor-grab"/>
        )}
        <button onClick={() => onView(t)} className="text-left group flex-1 min-w-0">
          <h3 className={`font-semibold text-gray-800 leading-snug group-hover:text-blue-600 transition-colors ${compact ? "text-xs" : "text-sm"} flex items-center gap-1`}>
            {t.title}
            <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-500 flex-shrink-0"/>
          </h3>
        </button>
      </div>
      <span className="shrink-0 flex items-center gap-1 flex-wrap justify-end">
        <SizeBadge size={calcTaskSize(t)} compact />
        {t.workLevel && (
          <span
            title={`Manager-classified: ${t.workLevel} (${t.workPoints}pt)`}
            className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
              t.workLevel === "critical" ? "bg-red-100 text-red-700 border border-red-200" :
              t.workLevel === "medium"   ? "bg-yellow-100 text-yellow-700 border border-yellow-200" :
                                            "bg-green-100 text-green-700 border border-green-200"
            }`}>
            {t.workLevel === "critical" ? "🔴" : t.workLevel === "medium" ? "🟡" : "🟢"} {t.workPoints}pt
          </span>
        )}
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_META[t.status]?.badge}`}>
          {STATUS_META[t.status]?.label}
        </span>
      </span>
    </div>

    {t.description && <p className="text-xs text-gray-500 line-clamp-2">{t.description}</p>}

    <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
      {t.assignedTo && <span className="flex items-center gap-1"><User size={10}/>{t.assignedTo.name}</span>}
      {t.totalLoggedMinutes > 0 && <span className="text-blue-500 font-medium flex items-center gap-1"><Clock size={10}/>{fmtMin(t.totalLoggedMinutes)} logged</span>}
    </div>

    {t.referenceLink && (
      <div className="flex flex-wrap gap-x-3 gap-y-0.5" onClick={e => e.stopPropagation()}>
        {parseLinks(t.referenceLink).filter(Boolean).map((lnk: string, i: number) => (
          <a key={i} href={lnk.startsWith("http") ? lnk : `https://${lnk}`} target="_blank" rel="noreferrer"
            className="flex items-center gap-1 text-xs text-blue-500 hover:underline w-fit">
            <Link size={10}/> Reference{parseLinks(t.referenceLink).filter(Boolean).length > 1 ? ` ${i+1}` : ""}
          </a>
        ))}
      </div>
    )}

    <select value={t.status} onChange={e => onStatus(t.id, e.target.value)}
      onClick={e => e.stopPropagation()}
      className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white text-gray-600">
      {STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
    </select>

    <div className="flex flex-wrap gap-1.5">
      <button onClick={() => onLog(t)}
        className="flex items-center gap-1 text-xs bg-blue-50 text-blue-600 border border-blue-200 px-2.5 py-1 rounded-lg hover:bg-blue-100 font-medium">
        <Clock size={11}/> Log Time
      </button>
      {canEditProp && (
        <button onClick={() => onEdit(t)}
          className="flex items-center gap-1 text-xs bg-gray-50 text-gray-600 border border-gray-200 px-2.5 py-1 rounded-lg hover:bg-gray-100 font-medium">
          <Pencil size={11}/> Edit
        </button>
      )}
      {canDeleteProp && (
        <button onClick={() => onDelete(t.id)}
          className="flex items-center gap-1 text-xs bg-red-50 text-red-500 border border-red-100 px-2.5 py-1 rounded-lg hover:bg-red-100 font-medium">
          <Trash2 size={11}/> Delete
        </button>
      )}
    </div>
  </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
const Tasks: React.FC = () => {
  const canEditTask   = usePermission("tasks", "edit");
  const canDeleteTask = usePermission("tasks", "delete");
  const [tasks,        setTasks]        = useState<any[]>([]);
  const [users,        setUsers]        = useState<any[]>([]);
  const [view,         setView]         = useState<"list" | "kanban">("list");
  const [statusFilter, setStatusFilter] = useState("");
  const [quickFilter, setQuickFilter] = useState<string>(() => {
    try { return localStorage.getItem("tasks.quickFilter") || "active"; } catch { return "active"; }
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [assigneeFilter, setAssigneeFilter] = useState<string>("");
  const [projectFilter,  setProjectFilter]  = useState<string>("");
  const [sizeFilter,     setSizeFilter]     = useState<string>(""); // SMALL | MEDIUM | LARGE
  const [deptFilter,     setDeptFilter]     = useState<string>("");
  const [dateFrom,       setDateFrom]       = useState<string>("");
  const [dateTo,         setDateTo]         = useState<string>("");
  const [projectsList,   setProjectsList]   = useState<any[]>([]);
  const [filtersOpen,    setFiltersOpen]    = useState(false);
  const [tick,         setTick]         = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTask,   setEditTask]   = useState<any | null>(null);
  const [logTask,    setLogTask]    = useState<any | null>(null);
  const [viewTaskId, setViewTaskId] = useState<string | null>(null);

  const [taskForm, setTaskForm] = useState({ ...blankTask });
  const [logForm,  setLogForm]  = useState({ ...blankLog  });

  const [newStOpen, setNewStOpen] = useState(false);
  const [stForm, setStForm] = useState({ title: "", description: "", taskId: "", assignedToId: "", status: "OPEN" });

  /* ── Drag & Drop state ─────────────────────────────────── */
  const [draggingId,   setDraggingId]   = useState<string | null>(null);
  const [dragOverCol,  setDragOverCol]  = useState<string | null>(null);
  const dragCounter = useRef<Record<string, number>>({});

  const submitSubTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stForm.taskId) return;
    await api.post(`/tasks/${stForm.taskId}/subtasks`, {
      title:        stForm.title,
      description:  stForm.description || undefined,
      assignedToId: stForm.assignedToId || undefined,
      status:       stForm.status,
    });
    setNewStOpen(false);
    setStForm({ title: "", description: "", taskId: "", assignedToId: "", status: "OPEN" });
    load();
  };

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const load = () => {
    const params: any = {};
    if (statusFilter)  params.status = statusFilter;
    if (quickFilter)   params.quickFilter = quickFilter;
    if (searchQuery)   params.search = searchQuery;
    if (assigneeFilter) params.assignedToId = assigneeFilter;
    if (projectFilter)  params.projectId = projectFilter;
    if (dateFrom)       params.dateFrom = dateFrom;
    if (dateTo)         params.dateTo = dateTo;
    api.get("/tasks", { params }).then(r => {
      const data = r.data;
      if (Array.isArray(data)) {
        setTasks(data);
        setTotalCount(data.length);
      } else {
        setTasks(data.tasks || []);
        setTotalCount(data.totalCount || 0);
      }
    });
  };
  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
    api.get("/projects").then(r => setProjectsList(r.data || [])).catch(() => {});
  }, [statusFilter, quickFilter, searchQuery, assigneeFilter, projectFilter, dateFrom, dateTo]);
  // Client-side filters for Size + Department (not supported server-side)
  const visibleTasks = React.useMemo(() => {
    return tasks.filter((t: any) => {
      if (sizeFilter) {
        const h = (t.totalLoggedMinutes ?? 0) / 60;
        const subs = (t.subTasks?.length ?? t._count?.subTasks ?? 0);
        const size = (h > 40 || subs >= 9) ? "LARGE" : (h <= 8 && subs <= 2) ? "SMALL" : "MEDIUM";
        if (size !== sizeFilter) return false;
      }
      if (deptFilter && t.department !== deptFilter) return false;
      return true;
    });
  }, [tasks, sizeFilter, deptFilter]);

  const resetFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setAssigneeFilter("");
    setProjectFilter("");
    setSizeFilter("");
    setDeptFilter("");
    setDateFrom("");
    setDateTo("");
    setQuickFilter("active");
  };


  // Persist quick filter choice
  useEffect(() => {
    try { localStorage.setItem("tasks.quickFilter", quickFilter); } catch {}
  }, [quickFilter]);

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { referenceLinks, ...rest } = taskForm;
      await api.post("/tasks", { ...rest, referenceLink: serializeLinks(referenceLinks) });
      toast.success("Task created!");
      setCreateOpen(false);
      setTaskForm({ ...blankTask });
      load();
    } catch { toast.error("Failed to create task"); }
  };

  const openEdit = (t: any) => {
    setEditTask(t);
    setTaskForm({
      title: t.title || "", description: t.description || "",
      department: t.department || "", assignedToId: t.assignedTo?.id || "",
      referenceLinks: parseLinks(t.referenceLink),
    });
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { referenceLinks, ...rest } = taskForm;
      await api.put(`/tasks/${editTask.id}`, { ...rest, referenceLink: serializeLinks(referenceLinks) });
      toast.success("Task updated!");
      setEditTask(null);
      setTaskForm({ ...blankTask });
      load();
    } catch { toast.error("Failed to update task"); }
  };

  const deleteTask = async (id: string) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/tasks/${id}`);
      toast.success("Task deleted!");
      if (viewTaskId === id) setViewTaskId(null);
      load();
    } catch { toast.error("Failed to delete task"); }
  };

  const startTimer = async (id: string) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, timerStart: new Date().toISOString() } : t));
    try { await api.post(`/tasks/${id}/start-timer`); toast.success("Timer started!"); load(); }
    catch { setTasks(prev => prev.map(t => t.id === id ? { ...t, timerStart: null } : t)); toast.error("Failed to start timer"); }
  };
  const stopTimer = async (id: string) => {
    const task = tasks.find(t => t.id === id);
    const elapsedSec = task?.timerStart
      ? Math.floor((Date.now() - new Date(task.timerStart).getTime()) / 1000) : 0;
    const elapsedMin = Math.max(1, Math.round(elapsedSec / 60));
    // Optimistic update: reset timer, add to logged
    setTasks(prev => prev.map(t => t.id === id
      ? { ...t, timerStart: null, timerTotal: 0, totalLoggedMinutes: (t.totalLoggedMinutes || 0) + elapsedMin }
      : t));
    try { await api.post(`/tasks/${id}/stop-timer`); toast.success("Timer stopped!"); load(); }
    catch { load(); toast.error("Failed to stop timer"); }
  };
  const openLog     = (t: any) => { setLogTask(t); setLogForm({ ...blankLog }); };

  const submitLog = async (e: React.FormEvent) => {
    e.preventDefault();
    const _mins = parseToMinutes(logForm.minutes, logForm.unit || "min"); if (!_mins || _mins < 1) { toast.error("Minimum 1 minute"); return; }
    try {
      const _pc = await api.get(`/comment-policy/check?minutes=${_mins}`);
      const _v: any = _pc.data || {};
      const _trim = (logForm.comment || "").trim();
      if (_v.required && (!_trim || _trim.length < (_v.minLength || 0))) {
        toast.error(`Comment of ${_v.minLength}+ characters required for ${_mins} min`); return;
      }
      if (!_v.required && _v.recommended && !_trim) {
        if (!window.confirm(`You worked ${_mins} minutes — a short comment is recommended. Save without one?`)) return;
      }
    } catch {}
    try {
      await api.post(`/tasks/${logTask.id}/work-logs`, {
        minutes: _mins, date: logForm.date, comment: logForm.comment,
      });
      toast.success("Time logged!");
      setLogTask(null);
      load();
    } catch (err: any) { toast.error(err?.response?.data?.message || "Failed to log time"); }
  };

  const changeStatus = async (id: string, status: string) => {
    // Optimistic update
    setTasks(prev => prev.map(t => t.id === id ? { ...t, status } : t));
    try {
      await api.put(`/tasks/${id}`, { status });
    } catch {
      toast.error("Failed to update status");
      load(); // revert
    }
  };

  /* ── Drag & Drop handlers ──────────────────────────────── */
  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("taskId", id);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverCol(null);
    dragCounter.current = {};
  };

  const handleDragEnter = (e: React.DragEvent, status: string) => {
    e.preventDefault();
    dragCounter.current[status] = (dragCounter.current[status] || 0) + 1;
    setDragOverCol(status);
  };

  const handleDragLeave = (e: React.DragEvent, status: string) => {
    dragCounter.current[status] = (dragCounter.current[status] || 1) - 1;
    if (dragCounter.current[status] <= 0) {
      dragCounter.current[status] = 0;
      setDragOverCol(prev => prev === status ? null : prev);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("taskId") || draggingId;
    dragCounter.current = {};
    setDragOverCol(null);
    setDraggingId(null);
    if (!id) return;
    const task = tasks.find(t => t.id === id);
    if (!task || task.status === targetStatus) return;
    changeStatus(id, targetStatus);
    toast.success(`Moved to ${STATUS_META[targetStatus]?.label}`);
  };

  /* ── Form fields ───────────────────────────────────────── */
  const formFields = (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
        <input value={taskForm.title} onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))} required
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea value={taskForm.description} onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))} rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
          <select value={taskForm.department} onChange={e => setTaskForm(f => ({ ...f, department: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
            <option value="">Select...</option>
            {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
          <select value={taskForm.assignedToId} onChange={e => setTaskForm(f => ({ ...f, assignedToId: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
            <option value="">Select...</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">Reference Link</label>
          <button type="button" onClick={() => setTaskForm(f => ({ ...f, referenceLinks: [...f.referenceLinks, ""] }))}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-0.5">+ Add Link</button>
        </div>
        {taskForm.referenceLinks.map((link: string, i: number) => (
          <div key={i} className="flex gap-1.5 mb-1.5">
            <input value={link} onChange={e => setTaskForm(f => {
              const updated = [...f.referenceLinks]; updated[i] = e.target.value; return { ...f, referenceLinks: updated };
            })} placeholder="https://..."
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
            {taskForm.referenceLinks.length > 1 && (
              <button type="button" onClick={() => setTaskForm(f => ({ ...f, referenceLinks: f.referenceLinks.filter((_: any, j: number) => j !== i) }))}
                className="text-gray-400 hover:text-red-500 px-1.5 transition-colors" title="Remove">
                <X size={14}/>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const cardProps = {
    tick,
    onView:   (t: any) => setViewTaskId(t.id),
    onStart:  startTimer,
    onStop:   stopTimer,
    onLog:    openLog,
    onEdit:   openEdit,
    onDelete: deleteTask,
    onStatus: changeStatus,
    canEdit:  canEditTask,
    canDelete: canDeleteTask,
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-gray-900">Tasks</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1">
            <button onClick={() => setView("list")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${view === "list" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <LayoutList size={13}/> List
            </button>
            <button onClick={() => setView("kanban")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${view === "kanban" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <Kanban size={13}/> Kanban
            </button>
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="🔍 Search title, description..."
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-56 focus:ring-2 focus:ring-blue-500 outline-none"
          />
          {view === "list" && (
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="">All Status</option>
              {STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
          )}
          <button onClick={() => { setCreateOpen(true); setTaskForm({ ...blankTask }); }}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">
            <Plus size={15}/> New Task
          </button>
          <button onClick={() => setNewStOpen(true)}
            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 text-sm font-medium">
            <Plus size={15}/> New Sub-Task
          </button>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      <div className="mb-3 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <span className="flex items-center gap-2">
            <Filter size={14}/>
            Advanced Filters
            {(assigneeFilter || projectFilter || sizeFilter || deptFilter || dateFrom || dateTo) && (
              <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">Active</span>
            )}
          </span>
          <span className="text-xs text-gray-400">{filtersOpen ? "▲" : "▼"}</span>
        </button>
        {filtersOpen && (
          <div className="px-4 pb-3 pt-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
              <option value="">All Assignees</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}{u.department ? ` — ${u.department}` : ""}</option>)}
            </select>
            <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
              <option value="">All Projects</option>
              {projectsList.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select value={sizeFilter} onChange={e => setSizeFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
              <option value="">All Sizes</option>
              <option value="SMALL">🟢 Small</option>
              <option value="MEDIUM">🟡 Medium</option>
              <option value="LARGE">🔴 Large</option>
            </select>
            <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
              <option value="">All Departments</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs"/>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs"/>
            <div className="col-span-2 md:col-span-3 lg:col-span-6 flex justify-end">
              <button type="button" onClick={resetFilters}
                className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1 rounded border border-gray-200 hover:bg-gray-50">
                Reset all filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Shared Filter Bar (visible in both List AND Kanban) */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {[
          { k: "mine-active",   label: "⭐ My Active" },
          { k: "active",        label: "🔥 Active (7d)" },
          { k: "week",          label: "📆 This Week" },
          { k: "my-open",       label: "📂 My Open" },
          { k: "completed-30d", label: "✅ Completed (30d)" },
          { k: "all",           label: "🗂 All" },
        ].map(f => (
          <button key={f.k} onClick={() => { setQuickFilter(f.k); setSearchQuery(""); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
              quickFilter === f.k && !searchQuery
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}>
            {f.label}
          </button>
        ))}
      </div>
      {/* Result counter */}
      <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
        <span>
          {searchQuery
            ? <>🔍 Searching all tasks — <strong className="text-gray-700">{visibleTasks.length}</strong> matches for "{searchQuery}"</>
            : <>Showing <strong className="text-gray-700">{visibleTasks.length}</strong> of <strong className="text-gray-700">{totalCount}</strong> tasks</>
          }
        </span>
        {(searchQuery || quickFilter !== "active") && (
          <button
            onClick={() => { setQuickFilter("active"); setSearchQuery(""); setStatusFilter(""); }}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Reset filters
          </button>
        )}
      </div>

      {/* List View */}
      {view === "list" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleTasks.map(t => <TaskCard key={t.id} t={t} {...cardProps}/>)}
          </div>
          {visibleTasks.length === 0 && (
            <div className="text-center py-12 text-gray-400 bg-white rounded-xl border border-gray-200">No tasks found</div>
          )}
        </>
      )}

      {/* Kanban View */}
      {view === "kanban" && (
        <div className="flex gap-4 overflow-x-auto pb-4 min-h-[500px]">
          {STATUSES.map(status => {
            const col = STATUS_META[status];
            const colTasks = visibleTasks.filter(t => t.status === status);
            const isOver = dragOverCol === status;

            return (
              <div
                key={status}
                className="flex flex-col shrink-0 w-72"
                onDragEnter={e => handleDragEnter(e, status)}
                onDragLeave={e => handleDragLeave(e, status)}
                onDragOver={handleDragOver}
                onDrop={e => handleDrop(e, status)}
              >
                {/* Column header */}
                <div className={`flex items-center gap-2 px-3 py-2.5 rounded-t-xl border transition-colors ${col.border} ${isOver ? col.dropBg : col.col}`}>
                  <span className={`w-2.5 h-2.5 rounded-full ${col.dot}`}/>
                  <span className="font-semibold text-gray-700 text-sm">{col.label}</span>
                  <span className="bg-white text-gray-500 text-xs font-medium px-2 py-0.5 rounded-full border border-gray-200 ml-auto">{colTasks.length}</span>
                </div>

                {/* Cards area */}
                <div className={`flex-1 rounded-b-xl border-x border-b p-2 space-y-2 min-h-[120px] transition-colors ${col.border}
                  ${isOver ? `${col.dropBg} ring-2 ring-inset ${
                    status === "OPEN"        ? "ring-gray-300"   :
                    status === "IN_PROGRESS" ? "ring-yellow-300" :
                    status === "ON_HOLD"     ? "ring-orange-300" :
                                              "ring-green-300"
                  }` : "bg-gray-50/60"}`}>

                  {/* Drop indicator at top when dragging */}
                  {isOver && draggingId && (
                    <div className={`h-1 rounded-full mb-1 ${
                      status === "OPEN"        ? "bg-gray-300"   :
                      status === "IN_PROGRESS" ? "bg-yellow-400" :
                      status === "ON_HOLD"     ? "bg-orange-400" :
                                                "bg-green-400"
                    }`}/>
                  )}

                  {colTasks.map(t => (
                    <TaskCard
                      key={t.id}
                      t={t}
                      compact
                      isDragging={draggingId === t.id}
                      onDragStart={handleDragStart}
                      {...cardProps}
                    />
                  ))}

                  {colTasks.length === 0 && !isOver && (
                    <div className="h-16 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-400">
                      Drop tasks here
                    </div>
                  )}

                  {isOver && (
                    <div className={`h-12 rounded-lg border-2 border-dashed flex items-center justify-center text-xs font-medium ${
                      status === "OPEN"        ? "border-gray-400 text-gray-500 bg-gray-50"     :
                      status === "IN_PROGRESS" ? "border-yellow-400 text-yellow-600 bg-yellow-50" :
                      status === "ON_HOLD"     ? "border-orange-400 text-orange-600 bg-orange-50" :
                                                "border-green-400 text-green-600 bg-green-50"
                    }`}>
                      ↓ Move to {col.label}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Hint for kanban */}
      {view === "kanban" && tasks.length > 0 && (
        <p className="text-xs text-gray-400 flex items-center gap-1">
          <GripVertical size={12}/> Drag cards between columns to change their status
        </p>
      )}

      {/* Detail Drawer */}
      {viewTaskId && (
        <TaskDetailDrawer
          taskId={viewTaskId}
          onClose={() => setViewTaskId(null)}
          onEdit={() => {
            const t = tasks.find(x => x.id === viewTaskId);
            if (t) { openEdit(t); setViewTaskId(null); }
          }}
        />
      )}

      {/* Create Modal */}
      {createOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-md my-4">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">New Task</h3>
              <button onClick={() => setCreateOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <form onSubmit={submitCreate} className="p-5">
              {formFields}
              <div className="flex gap-3 pt-4 mt-2 border-t border-gray-100">
                <button type="button" onClick={() => setCreateOpen(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
                <button type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">Create Task</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-md my-4">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Edit Task</h3>
              <button onClick={() => setEditTask(null)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <form onSubmit={submitEdit} className="p-5">
              {formFields}
              <div className="flex gap-3 pt-4 mt-2 border-t border-gray-100">
                <button type="button" onClick={() => setEditTask(null)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
                <button type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Log Time Modal */}
      {logTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Log Time</h3>
                <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{logTask.title}</p>
              </div>
              <button onClick={() => setLogTask(null)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <form onSubmit={submitLog} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time Logged *</label>
                <div className="flex gap-2">
                  <input type="text" value={logForm.minutes}
                    onChange={e => setLogForm(f => ({ ...f, minutes: e.target.value }))}
                    required placeholder={logForm.unit==="hr" ? "e.g. 1.5" : "e.g. 30"}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
                  <div className="flex border border-gray-300 rounded-lg overflow-hidden text-sm">
                    <button type="button" onClick={() => setLogForm(f=>({...f,unit:"min"}))}
                      className={`px-3 py-2 font-medium transition-colors ${(logForm.unit||"min")==="min"?"bg-blue-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>min</button>
                    <button type="button" onClick={() => setLogForm(f=>({...f,unit:"hr"}))}
                      className={`px-3 py-2 font-medium transition-colors ${logForm.unit==="hr"?"bg-blue-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>hr</button>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">Also accepts: 1h 30m · 1.5 · 90</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input type="date" value={logForm.date} onChange={e => setLogForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
                <textarea value={logForm.comment} onChange={e => setLogForm(f => ({ ...f, comment: e.target.value }))}
                  rows={2} placeholder="What did you work on?"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
              </div>
              <div className="flex gap-3 pt-1 border-t border-gray-100">
                <button type="button" onClick={() => setLogTask(null)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
                <button type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">Log Time</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Sub-Task Modal */}
      {newStOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">New Sub-Task</h3>
              <button onClick={() => setNewStOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <form onSubmit={submitSubTask} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" required value={stForm.title}
                  onChange={e => setStForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Sub-task title"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 outline-none"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent Task *</label>
                <select required value={stForm.taskId}
                  onChange={e => setStForm(f => ({ ...f, taskId: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 outline-none">
                  <option value="">Select task...</option>
                  {tasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                <select value={stForm.assignedToId}
                  onChange={e => setStForm(f => ({ ...f, assignedToId: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 outline-none">
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={stForm.status}
                  onChange={e => setStForm(f => ({ ...f, status: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 outline-none">
                  <option value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="ON_HOLD">On Hold</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={stForm.description}
                  onChange={e => setStForm(f => ({ ...f, description: e.target.value }))}
                  rows={2} placeholder="Optional description..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 outline-none"/>
              </div>
              <div className="flex gap-3 pt-1 border-t border-gray-100">
                <button type="button" onClick={() => setNewStOpen(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
                <button type="submit"
                  className="flex-1 bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 text-sm font-medium">Create Sub-Task</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* (Edit Sub-Task is handled inside TaskDetailDrawer) */}

    </div>
  );
};

export default Tasks;
